// CrawlStep.tsx — onboarding step 2 (spec D6, handoff §6 step "Crawl"). Purely
// presentational: `Onboarding.tsx` owns the 3s source-polling query (same pattern as
// `BotSources.tsx`) and passes the primary source down. Progress bar phase mapping and the
// "never a dead end" CTA are the two load-bearing rules from the M5 task-5 contract:
//   pending 10% / crawling 45% / indexing 80% / ready 100%, width transition <=200ms ease.
//   The "Try your bot" CTA is ALWAYS enabled (regardless of status/error) so a slow or
//   failed crawl never blocks the rest of onboarding.
//
// prod-copy-fixes cycle (Johnny, found testing on dev, PR #13) — three defects fixed here:
//
//   1. `source.pageCount` is only written by the crawler on its FINAL status transition (see
//      `apps/api/src/jobs/handlers/crawl-source.ts`), so it is `0` for the entire `crawling`
//      and `indexing` phases. This file used to print "0 pages read so far" next to a bar
//      already at 45%/80%, and a synthetic tick list ("✓ N pages read", appended "as pageCount
//      grows") that consequently could only ever grow once, at completion — it had never
//      actually ticked during a crawl. Fixed by never rendering a page count until
//      `status === 'ready'`, when it's real, and by DELETING the tick list rather than leaving
//      a feature that cannot work without per-page data the API doesn't expose. (Making it work
//      would mean the crawler streaming per-page progress — out of scope for a copy fix; if
//      that lands later, a tick list can come back for real.)
//   2. A static bar that only moves at three points in several minutes reads as frozen —
//      Johnny's own words, pointing at Claude Code's rotating status verbs as the reference.
//      `t.onboarding.crawlStatusLines` (below) rotates a short, phase-truthful line every ~2s: what pending/
//      crawling/indexing genuinely means, never a specific claim we can't back ("reading your
//      pricing page" — we don't know which page). The bouncing dots are the same element that
//      used to sit under the tick list (unchanged, still `motion-reduce`-safe); no new spinner.
//   3. A zero-page crawl rendered the server's `ZERO_PAGES_MESSAGE`, which tells the visitor to
//      "enable rendered mode" — a control that only exists on the Sources screen, later in the
//      product. Onboarding has no such toggle, so that was the one actionable line in a message
//      for a screen the visitor isn't on. The most likely cause of a zero-page crawl during
//      onboarding is a typo'd domain (how Johnny hit this — thinkswarm.com vs .net), so the
//      error now leads with that and offers a real fix: an editable URL field, pre-filled with
//      the failed source's own URI, that replaces it on the SAME bot (delete the failed source,
//      create a fresh one) rather than routing back through the Stepper's step 1 — `WebsiteStep`
//      creates a NEW bot on submit, and `PLAN_LIMITS.free.maxBots` is 1, so "go back and fix the
//      URL" would walk a Free visitor into a bot-limit error instead of fixing anything. Retry
//      stays, for the "URL was right, the site just blinked" case. Rendered mode is now
//      mentioned as a fact about what's available after setup, not an instruction to follow here.
import { useEffect, useRef, useState } from 'react';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import type { SourceDto, SourceStatus } from '@repo/shared-types';
import { normalizeUrl } from '../../lib/normalize-url';
import { useT } from '../../locales/context';

// `deleting` is excluded same as `error` — a source being torn down mid-onboarding isn't a
// crawl phase to show progress for, and `phase` below never selects it.
const PHASE_PERCENT: Record<Exclude<SourceStatus, 'error' | 'deleting'>, number> = {
  pending: 10,
  crawling: 45,
  indexing: 80,
  ready: 100,
};

const ROTATE_MS = 2000;

// Defect 2 — every line describes a mechanism the system is genuinely doing in that phase, not
// an invented specific. `pending` is "queued, not started yet"; `crawling` is discovery + fetch;
// `indexing` is turning fetched pages into something searchable. No line names a page, a count,
// or anything else we don't actually know. The vocabulary itself lives in
// `t.onboarding.crawlStatusLines` (locale-dashboard plan, Task 6).
type SyncingStatus = Exclude<SourceStatus, 'error' | 'deleting' | 'ready'>;

/** Own component, rendered with `key={phase}` below — mounting fresh whenever the PHASE
 * changes (not on every poll of the same phase) is what resets `lineIndex` to 0, via React's
 * ordinary mount behavior rather than an effect calling `setState` synchronously on every
 * dependency change (the eslint `react-hooks/set-state-in-effect` rule flags exactly that
 * pattern — a `key` remount is the idiomatic replacement for "reset local state when this prop
 * changes"). The effect itself only ever calls `setState` from the interval's own callback,
 * which is the "subscribe to an external clock" shape the rule expects. */
function RotatingStatusLine({ phase }: { phase: SyncingStatus }) {
  const t = useT();
  const [lineIndex, setLineIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setLineIndex((i) => i + 1), ROTATE_MS);
    return () => clearInterval(id);
  }, []);
  const lines = t.onboarding.crawlStatusLines[phase];
  return <span data-testid="crawl-status-line">{lines[lineIndex % lines.length]}</span>;
}

export function CrawlStep({
  hostname,
  source,
  sourcesPending,
  sourcesError,
  onRetry,
  retrying,
  retryError,
  onReplaceUrl,
  replacingUrl,
  replaceUrlError,
  onNext,
}: {
  hostname: string;
  source: SourceDto | undefined;
  sourcesPending: boolean;
  sourcesError: Error | null;
  onRetry: () => void;
  retrying: boolean;
  retryError: string | null;
  /** Defect 3 — deletes the failed source and creates a fresh one on the same bot with this
   * (already-normalized) URL. Owned by `Onboarding.tsx`, mirroring `onRetry`. */
  onReplaceUrl: (uri: string) => void;
  replacingUrl: boolean;
  replaceUrlError: string | null;
  onNext: () => void;
}) {
  const t = useT();
  const isError = source?.status === 'error';
  const isReady = source?.status === 'ready';
  const phase =
    source && source.status !== 'error' && source.status !== 'deleting' ? source.status : null;
  const percent = phase ? PHASE_PERCENT[phase] : 0;
  const syncingPhase: SyncingStatus | null = phase && phase !== 'ready' ? phase : null;

  // Defect 3 — the replace-URL field starts pre-filled with the failed source's own URI, and
  // only resets when the SOURCE ITSELF changes (a genuinely different id — e.g. after a
  // successful replace swaps in the new source), never on every re-render, so an in-progress
  // edit survives incidental parent re-renders.
  const [replaceUrlValue, setReplaceUrlValue] = useState(source?.uri ?? '');
  const [replaceUrlValidationError, setReplaceUrlValidationError] = useState<string | null>(null);
  const lastSourceIdRef = useRef(source?.id);
  useEffect(() => {
    if (source?.id !== lastSourceIdRef.current) {
      lastSourceIdRef.current = source?.id;
      setReplaceUrlValue(source?.uri ?? '');
      setReplaceUrlValidationError(null);
    }
  }, [source?.id, source?.uri]);

  function handleReplaceUrlSubmit(e: React.FormEvent) {
    e.preventDefault();
    const normalized = normalizeUrl(replaceUrlValue);
    if (!normalized) {
      setReplaceUrlValidationError(t.onboarding.invalidWebsiteUrl);
      return;
    }
    setReplaceUrlValidationError(null);
    onReplaceUrl(normalized);
  }

  return (
    <div className="flex flex-col gap-5 rounded-[14px] border bg-card p-8 shadow-el-1">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[26px] font-bold tracking-[-0.01em]">
          {t.onboarding.crawlHeading({ hostname })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isReady ? t.onboarding.crawlSubtitleReady : t.onboarding.crawlSubtitleSyncing}
        </p>
      </div>

      {/* Sized to the progress bar + tick list this replaces once `source` resolves (handoff
          Shell Anatomy §06), not a bare loading label. */}
      {sourcesPending && !source && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <SkeletonText className="w-28" />
            <SkeletonText className="w-8" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      )}

      {isError && (
        <div className="flex flex-col gap-3 rounded-[10px] border border-destructive/30 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{t.onboarding.crawlErrorLead}</p>

          <form onSubmit={handleReplaceUrlSubmit} className="flex flex-col gap-1.5">
            <Label htmlFor="ob-replace-url">{t.onboarding.websiteUrlLabel}</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              {/* `type="text"`, not `"url"` — deliberately: native URL constraint validation
                  requires a scheme, which would silently block submitting a bare hostname
                  ("thinkswarm.net") even though `normalizeUrl` below is written to accept
                  exactly that, the same forgiving behavior `WebsiteStep`'s own field offers. */}
              <Input
                id="ob-replace-url"
                type="text"
                value={replaceUrlValue}
                onChange={(e) => setReplaceUrlValue(e.target.value)}
                aria-invalid={!!replaceUrlValidationError}
                className="sm:flex-1"
              />
              <Button type="submit" variant="outline" size="sm" disabled={replacingUrl}>
                {replacingUrl ? t.onboarding.crawlReplaceUrlUpdating : t.onboarding.crawlReplaceUrlCta}
              </Button>
            </div>
            {replaceUrlValidationError ? (
              <span className="text-xs text-destructive">{replaceUrlValidationError}</span>
            ) : (
              <span className="text-xs text-muted-foreground">
                {t.onboarding.crawlReplaceUrlHelper}
              </span>
            )}
            {replaceUrlError && <p className="text-xs text-destructive">{replaceUrlError}</p>}
          </form>

          <div className="flex flex-col gap-1.5 border-t pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit"
              disabled={retrying}
              onClick={onRetry}
            >
              {retrying ? t.onboarding.crawlRetrying : t.onboarding.crawlRetryCta}
            </Button>
            {retryError && <p className="text-xs text-destructive">{retryError}</p>}
            <span className="text-xs text-muted-foreground">{t.onboarding.crawlRetryHelper}</span>
          </div>
        </div>
      )}

      {!isError && source && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-[12.5px]">
            <span className="flex items-center gap-2 text-muted-foreground">
              {isReady ? (
                t.onboarding.crawlPagesIndexed({ n: source.pageCount })
              ) : (
                <>
                  {syncingPhase && <RotatingStatusLine key={syncingPhase} phase={syncingPhase} />}
                  <span className="inline-flex gap-1" aria-hidden="true">
                    <span className="size-1 animate-bounce rounded-full bg-primary motion-reduce:animate-none" />
                    <span className="size-1 animate-bounce rounded-full bg-primary [animation-delay:150ms] motion-reduce:animate-none" />
                    <span className="size-1 animate-bounce rounded-full bg-primary [animation-delay:300ms] motion-reduce:animate-none" />
                  </span>
                </>
              )}
            </span>
            <span className="font-medium text-primary">{percent}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out motion-reduce:transition-none"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      )}

      {!isError && !source && !sourcesPending && (
        <p className="text-sm text-muted-foreground">
          {sourcesError ? sourcesError.message : t.onboarding.crawlNoSourceYet}
        </p>
      )}

      <Button type="button" onClick={onNext} className="h-[42px] w-full">
        {isReady ? t.onboarding.crawlCtaReady : t.onboarding.crawlCtaSyncing}
      </Button>
    </div>
  );
}
